"use client";
import type { JSX } from "react";
import { useState } from "react";
import { motion, type Variants } from "framer-motion";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/home-v3/ui/accordion";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { CTA_GRADIENT_BACKGROUND_CLASSNAME } from "@/lib/cta-styles";

const headingVariants: Variants = {
  hidden: { opacity: 0, filter: "blur(8px)" },
  visible: {
    opacity: 1,
    filter: "blur(0px)",
    transition: { duration: 0.8, ease: [0.16, 1, 0.3, 1] },
  },
};

const faqItems = [
  {
    question:
      "What is the difference between bookkeeping and accounting in HK?",
    answer:
      "Bookkeeping is the ongoing record of your transactions, the general ledger, receipts and bank activity as they happen. Accounting takes that record and turns it into financial statements and management reports, reconciled and structured to a recognised standard such as HKFRS or HK SME-FRS. A company can have accurate bookkeeping and still not have proper accounts prepared, which is usually what causes delays at audit or filing time.",
  },
  {
    question: "Are your accountants qualified?",
    answer:
      "Yes. Your accounts are prepared directly by our certified accountants, not passed to an outside party. HKICPA qualification means the accountant has met the training, examination and ongoing professional standards set by the Hong Kong Institute of Certified Public Accountants, the body regulating the accounting profession in Hong Kong.",
  },
  {
    question: "Do I get a dedicated accountant?",
    answer:
      "You get a dedicated back end team supporting your account, with a department head and senior lead as your point of contact for guidance and questions.",
  },
  {
    question: "Will I get access to accounting software like Xero or QuickBooks?",
    answer:
      "Your monthly performance and full transaction listing are provided through a dashboard we maintain for you. If you specifically want access to accounting software such as Xero or QuickBooks, this can be arranged at an additional cost.",
  },
  {
    question: "Can you prepare accounts if someone else does my bookkeeping?",
    answer:
      "Yes. We can work from records kept elsewhere. We review them against your bank and payment activity first and flag anything that needs correcting before we prepare your accounts.",
  },
  {
    question: "Do I need audited accounts even if my company made no profit?",
    answer:
      "Generally yes. Hong Kong's audit and filing requirements are not based on whether the company made a profit. If your company has little or no trading activity, ask us how that affects your specific position.",
  },
  {
    question: "Who performs the actual audit?",
    answer:
      "This page covers preparing your accounts, your financial statements, reconciliations and management reports. The audit itself, where an independent Certified Public Accountant examines and signs off those statements, is a separate, specifically licensed service.",
  },
  {
    question: "How do you keep my accounting records secure and organised?",
    answer:
      "Your records and dashboard are kept in one place rather than spread across email and downloads, so they stay retrievable for as long as Hong Kong requires you to keep them.",
  },
  {
    question: "Can you help if I am behind on getting proper accounts prepared?",
    answer:
      "Yes. We review where things currently stand and bring your accounts up to date before your next audit or filing deadline arrives.",
  },
  {
    question: "Do you also handle my Profits Tax Return and Annual Return?",
    answer:
      "We can also assist with your Hong Kong profits tax return and annual return. However, this page is specifically about preparing the accounts those filings depend on.",
  },
];

const INITIAL_VISIBLE_COUNT = 8;

export const FrequentlyAskedQuestionsSection = (): JSX.Element => {
 const [showAll, setShowAll] = useState(false);
  const visibleFaqItems = showAll
    ? faqItems
    : faqItems.slice(0, INITIAL_VISIBLE_COUNT);

  return (
    <section
      aria-labelledby="faq-heading"
      className="relative flex w-full flex-col max-sm:mt-20 items-center gap-14 px-4 pt-6 sm:px-6"
    >
      <header className="flex w-full flex-col items-center gap-6">
        <Badge className="animate-revolving-border rounded-[30px] border-0 bg-[#6a6a6a80] px-4 py-2 [font-family:'Suisse_Intl_Trial-Medium',Helvetica] text-sm font-medium leading-[23.4px] text-white backdrop-blur-[3.5px] backdrop-brightness-100">
          Have questions?
        </Badge>
        <motion.h2
          id="faq-heading"
          className="[font-family:'Suisse_Works_Trial-Regular',Helvetica] text-center text-4xl font-normal leading-[1.15] tracking-[-1.92px] text-white sm:text-5xl sm:leading-[68px]"
          variants={headingVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.6 }}
        >
          Hong Kong Accounting Services FAQs
        </motion.h2>
      </header>
      <div className="flex w-full max-w-[800px] flex-col">
        <Accordion
          type="single"
          collapsible
          className="flex w-full flex-col gap-[13px]"
        >
          {visibleFaqItems.map(({ question, answer }, index) => (
            <AccordionItem
              key={question}
              value={`faq-${index + 1}`}
              className="relative overflow-hidden rounded-[20px] border border-[#ffffff29] bg-[#0f1218]"
            >
              <span
                aria-hidden="true"
                className="pointer-events-none absolute -top-[138px] left-[calc(50%+240px)] h-[171px] w-[171px] rounded-full bg-[#6fa6ff] blur-[71.73px]"
              />
              <AccordionTrigger className="relative z-10 flex min-h-[80px] w-full items-center justify-between gap-4 px-6 max-sm:py-3 py-6 text-left no-underline hover:no-underline [&>svg]:h-8 [&>svg]:w-8 [&>svg]:shrink-0 [&>svg]:text-white">
                <span className="[font-family:'Suisse_Intl_Trial-Medium',Helvetica] max-sm:text-base text-xl font-medium leading-7 tracking-[0] text-white">
                  {question}
                </span>
              </AccordionTrigger>
              <AccordionContent className="relative z-10 px-6 pb-6 pt-0 [font-family:'Suisse_Intl_Trial-Regular',Helvetica] max-sm:text-sm text-base font-normal leading-6 text-[#ffffffb2]">
                {answer}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
        {faqItems.length > INITIAL_VISIBLE_COUNT && (
          <div className="flex justify-center pt-14">
            <Button
              type="button"
              onClick={() => setShowAll((prev) => !prev)}
              className={`h-auto gap-2 rounded-3xl px-6 py-4 font-inter text-sm font-medium leading-4 text-white ${CTA_GRADIENT_BACKGROUND_CLASSNAME}`}
            >
              {showAll ? "Show Less" : "Show More"}
              <img
                className={`h-4 w-4 transition-transform ${showAll ? "rotate-180" : ""}`}
                alt=""
                aria-hidden="true"
                src="/home-v3/component-1-3.svg"
              />
            </Button>
          </div>
        )}
      </div>
    </section>
  );
};
